import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import MojitoCore from 'mojito/core';
import { selectStatus } from 'applications/sports/stores/application/selectors.js';

const { PAGE_READY, PAGE_INTERACTED } = MojitoCore.Services.Performance.types.TIMELINE_RECORD_TYPES;

/**
 * Sportsbook application view hooks.
 *
 * @class AppHooks
 * @name AppHooks
 * @memberof Mojito.Applications.Sports
 */

/**
 * Returns true once deferred application UI can be rendered: when {@link Mojito.Core.Services.Performance.types.TIMELINE_RECORD_TYPES|PAGE_READY} or {@link Mojito.Core.Services.Performance.types.TIMELINE_RECORD_TYPES|PAGE_INTERACTED} has been reported, or after the optional timeout elapses.
 *
 * @function usePageReady
 *
 * @param {number} timeout - Maximum time in milliseconds to wait before treating the page as ready when performance metrics have not been reported. Pass 0 to disable the timeout fallback.
 *
 * @returns {boolean} True when deferred content can be rendered.
 * @memberof Mojito.Applications.Sports.AppHooks
 */
const usePageReady = timeout => {
    const status = useSelector(selectStatus);
    const [timedOut, setTimedOut] = useState(false);
    const isPageReady = status === PAGE_READY || status === PAGE_INTERACTED || timedOut;

    useEffect(() => {
        if (isPageReady || !timeout) {
            return;
        }
        const timerId = setTimeout(() => setTimedOut(true), timeout);
        return () => clearTimeout(timerId);
    }, [isPageReady, timeout]);

    return isPageReady;
};

export { usePageReady };
